import { ReactNode, useEffect, useState } from 'react';
import './header.css';
import { GetAccessToken, HasRole, Role } from '../../utils/auth-utils';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Avatar, Dropdown, Select, MenuProps, Button, App } from 'antd';
import { MenuOutlined, RiseOutlined, SmileOutlined, TrophyOutlined } from '@ant-design/icons';
import { scopes } from '../../authConfig';
import { strings } from '../../lang';
import { datadogRum } from '@datadog/browser-rum';
import { useHeaderState } from '../../store/header-state';
import { Link } from 'react-router';
import { UserDataDto } from '../../dto/reporting-models';
import { reportingService } from '../../services/reporting-service';
import { useShallow } from 'zustand/shallow';
import { ThemeToggle } from '../common/theme-toggle';

interface HeaderProps {
  optionalComponent?: ReactNode;
  centerComponents?: ReactNode;
  changeUserInterface?: (user: UserDataDto) => void;
  hideNavigationMenu?: boolean;
}

export const Header = ({ optionalComponent, centerComponents, changeUserInterface, hideNavigationMenu }: HeaderProps) => {
  const avatarColorList = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae'];
  const { instance, inProgress, accounts } = useMsal();
  const { notification } = App.useApp();
  const [accountMenuItems, setAccountMenuItems] = useState<any>();
  const [userInitials, setUserInitials] = useState<string>();
  const [color, setColor] = useState(avatarColorList[0]);
  const isAuthenticated = useIsAuthenticated();
  const setLang = useHeaderState(useShallow((state) => state.setLanguage));
  const theme = useHeaderState(useShallow((state) => state.theme));
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('isAdmin') === 'true');
  const [isManager, setIsManager] = useState(false);
  const [isInboundAgent, setIsInboundAgent] = useState(false);
  const [isReportingAgent, setIsReportingAgent] = useState(false);
  const [isContactEditor, setIsContactEditor] = useState(false);
  const [userInfo, setUserInfo] = useState<UserDataDto | null>(null);
  const [newUserInfo, setNewUserInfo] = useState<UserDataDto | null>(null);

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      datadogRum.setUser({
        id: accounts[0].homeAccountId,
        name: accounts[0].name,
        email: accounts[0].username,
      });
      const adminStatus = HasRole(accounts, Role.BookingAdmin);
      setIsAdmin(adminStatus);
      localStorage.setItem('isAdmin', adminStatus.toString());
      setIsReportingAgent(HasRole(accounts, Role.LiveReporter));
      setIsManager(HasRole(accounts, Role.BookingManager));
      setIsInboundAgent(HasRole(accounts, Role.InboundAgent));
      setIsContactEditor(HasRole(accounts, Role.ContactEditor));
    }
  }, [accounts]);

  useEffect(() => {
    if ((HasRole(accounts, Role.BookingAdmin) || HasRole(accounts, Role.LiveReporter)) && window.location.pathname === '/booking') {
      getUserActionsAsync();
      const intervalId = setInterval(() => {
        getUserActionsAsync();
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, []);

  useEffect(() => {
    if (isAdmin || isReportingAgent) {
      if (changeUserInterface && newUserInfo) {
        changeUserInterface(newUserInfo);
      }
      if (userInfo === null) {
        setUserInfo(newUserInfo);
      } else {
        // get diff between actions and userActions by when and show message for every diff
        if (newUserInfo?.selectedBookingButton !== userInfo?.selectedBookingButton) {
          openActionNotification(strings.reportingNotificationActionTitle, `Congratulation, you received ${newUserInfo?.selectedBookingButton?.replace(/([A-Z])/g, ' $1').trim()} for Level Up!`);
        }

        if (userInfo?.userActions && newUserInfo?.userActions) {
          const sortedUserActions = userInfo.userActions.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
          const oldLatestActionDate = sortedUserActions[0];
          const newActions = oldLatestActionDate
            ? newUserInfo?.userActions?.filter((x) => new Date(x.when) > new Date(oldLatestActionDate.when) && x.points > 0)
            : newUserInfo?.userActions?.filter((x) => x.points > 0);
          for (const action of newActions!) {
            openActionNotification(
              strings.reportingNotificationActionTitle,
              `+${action.points}XP ${strings.getActionName(
                action.actionType as
                  | 'Booked'
                  | 'NotReached'
                  | 'ReachedNotBooked'
                  | 'CustomerLost'
                  | 'RevisionQueueAssignment'
                  | 'WrongPhoneNumber'
                  | 'AppointmentHappened'
                  | 'Assignment'
                  | 'WentOffline',
              )}`,
            );
          }
        }

        if (userInfo?.currentLevel !== newUserInfo?.currentLevel) {
          openLvlNotification('Level up!', strings.reportingNotificationLevelUp(newUserInfo?.currentLevel?.toString(), newUserInfo?.levelName!));
        }

        if (userInfo?.slotsForToday && newUserInfo?.slotsForToday) {
          const oldSlots = userInfo?.slotsForToday?.filter((slot) => slot.status === 'Completed');
          const slotsToShow = newUserInfo?.slotsForToday?.filter((slot) => slot.status === 'Completed' && !oldSlots?.some((oldSlot) => oldSlot.externalAppointmentId === slot.externalAppointmentId));
          for (const slot of slotsToShow!) {
            let formattedStartTime = undefined;
            let whenBookedFormatted = undefined;
            if (slot.startTime) {
              const startTimeDate = new Date(slot.startTime);
              formattedStartTime = new Intl.DateTimeFormat(document.documentElement.lang === 'de' ? 'de-DE' : 'en-EN', {
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: false,
              }).format(startTimeDate);
            }

            openSlotNotification(strings.reportingNotificationCustomerShowedTitle, strings.reportingNotificationCustomerShowedFunc(formattedStartTime, whenBookedFormatted));
          }
        }

        if (userInfo?.achievements && newUserInfo?.achievements) {
          const alreadyCompleted = userInfo?.achievements.filter((x) => x.isCompleted);
          const newCompleted = newUserInfo?.achievements.filter((x) => x.isCompleted);
          const newAchievements = newCompleted.filter((newAch) => !alreadyCompleted.some((oldAch) => oldAch.id === newAch.id));

          for (const achievement of newAchievements) {
            //TODO: Might need to add different icon for achievements
            openActionNotification(strings.reportingNotificationAchievementTitle, strings.reportingNotificationAchievement(achievement.name, achievement.points.toString()));
          }
        }

        setUserInfo(newUserInfo!);
      }
    }
  }, [newUserInfo]);

  useEffect(() => {
    if (isAuthenticated) {
      GetAccessToken(instance, inProgress, scopes.salesAppointmentApi).then(async (token) => {
        if (token) {
          let firstLetter = 'U';
          try {
            const initials = token.account!.name!.split(/ /g);
            firstLetter = initials[0][0];
            setUserInitials(`${initials[0][0]} ${initials[1][0]}`);

            const codePoint = firstLetter.charCodeAt(0);
            const index = codePoint % avatarColorList.length;
            setColor(avatarColorList[index]);
          } catch {
            setUserInitials('U');
          }

          setAccountMenuItems([
            {
              key: '1',
              label: <Link to="/profile">{token.account.name}</Link>,
              disabled: !(isAdmin || isReportingAgent),
            },
            {
              key: '2',
              label: 'Logout',
              onClick: () => {
                instance.logoutRedirect({
                  account: instance.getActiveAccount(),
                });
              },
            },
          ]);
        }
      });
    }
  }, [isAuthenticated, isAdmin, isReportingAgent]);

  const openSlotNotification = (title: string, description: string) => {
    notification.open({
      message: title,
      description: description,
      showProgress: true,
      icon: <TrophyOutlined style={{ color: 'var(--color-gold)' }} />,
    });
  };

  const openActionNotification = (title: string, description: string) => {
    notification.open({
      message: title,
      description: description,
      showProgress: true,
      icon: <SmileOutlined style={{ color: 'var(--color-primary)' }} />,
    });
  };

  const openLvlNotification = (title: string, description: string) => {
    notification.open({
      message: title,
      description: description,
      showProgress: true,
      icon: <RiseOutlined style={{ color: 'var(--color-primary)' }} />,
    });
  };

  const getUserActionsAsync = async () => {
    if (isAuthenticated) {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      if (token) {
        try {
          const response = await reportingService.getUserDataAsync(true, token.accessToken);
          setNewUserInfo(response);
        } catch (e) {}
      }
    }
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: <Link to="/booking">Booking</Link>,
    },
  ];
  if (isAdmin || isReportingAgent) {
    items.push({
      key: '3',
      label: <Link to="/profile">Profile</Link>,
    });
  }
  if (isAdmin || isManager || isReportingAgent) {
    items.push({
      key: '2',
      label: <Link to="/reporting">Reporting</Link>,
    });
  }
  if (isAdmin || isInboundAgent) {
    items.push({
      key: '5',
      label: <Link to="/inbound">Inbound Center</Link>,
    });
  }
  if (isManager || isAdmin) {
    items.push(
      {
        key: '4',
        label: <Link to="/exports">Exports</Link>,
      },
      {
        key: '7',
        label: <Link to="/conflicts">Conflicts</Link>,
      },
      {
        key: '9',
        label: <Link to="/ums">Team Management</Link>,
      },
      {
        key: '14',
        label: <Link to="/priority-breakdown">Priority Breakdown</Link>,
      },
    );
  }
  if (isAdmin || isContactEditor) {
    items.push({
      key: '16',
      label: <Link to="/edit-contact">Edit Contacts</Link>,
    });
  }
  if (isAdmin) {
    items.push({
      key: '6',
      label: <Link to="/bulk-import-opportunities">Bulk Import Opportunities</Link>,
    });
  }
  if (isAdmin || isManager) {
    items.push({
      key: '10',
      label: <Link to="/sales-agents">Sales Agents</Link>,
    });
  }
  if (isAdmin || isManager) {
    items.push({
      key: '11',
      label: <Link to="/qualification-config">Qualification Config</Link>,
    });
  }
  if (isAdmin) {
    items.push(
      {
        key: '12',
        label: <Link to="/lost-reason-config">Lost reasons</Link>,
      },
      {
        key: '15',
        label: <Link to="/gamification-config/experience-points">{strings.gamificationSettings.title}</Link>,
      },
      {
        key: '13',
        label: <Link to="/autodialer-settings/bucket-config">{strings.autodialerSettings.title}</Link>,
      },
      {
        key: '8',
        label: <Link to="/admin">Admin Page</Link>,
      },
    );
  }
  return (
    <header className="header">
      <div className="header__logo">
        {!hideNavigationMenu && (
          <Dropdown menu={{ items }}>
            <Button icon={<MenuOutlined />} />
          </Dropdown>
        )}
        <img className="header__img" src={theme === 'dark' ? '/logo-v2-dark.png' : '/logo-v2.png'} alt="booking-os" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>{centerComponents}</div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {optionalComponent}
        <ThemeToggle />
        <Select
          className="header__lang"
          defaultValue={['de', 'en'].includes(strings.getLanguage()) ? strings.getLanguage() : 'de'}
          options={[
            { value: 'de', label: 'DE' },
            { value: 'en', label: 'EN' },
          ]}
          onChange={(lang) => {
            document.documentElement.lang = lang;
            strings.setLanguage(lang);
            setLang(lang);
          }}
        ></Select>
        <Dropdown
          className="header__avatar"
          menu={{
            items: accountMenuItems,
            selectable: true,
            defaultSelectedKeys: ['3'],
          }}
        >
          <Avatar className="header__avatar-icon" style={{ backgroundColor: color, verticalAlign: 'middle' }} size="large">
            {userInitials}
          </Avatar>
        </Dropdown>
      </div>
    </header>
  );
};
